import {
  ApplicantAnotherExperience,
  ApplicantArticle,
  ApplicantAward,
  ApplicantCommunity,
  ApplicantContest,
  ApplicantEducation,
  ApplicantMentorship,
  ApplicantOpenSource,
  ApplicantPatent,
  ApplicantPublicTalk,
  ApplicantReferee,
} from "@prisma/client";
import { DropdownOption } from "immigram-ui-cmon/src/components/Dropdown/Dropdown";
import { TFunction } from "next-i18next";

import { ApplicationSectionStatusType } from "@/lib/applicationSectionStatus";
import { ExperienceType } from "@/lib/experienceType";
import {
  ApplicantMilestoneFull,
  ApplicantProjectFull,
  ApplicationFormPrepared,
} from "@/lib/prisma";
import { parseProseMirrorSchema } from "@/lib/proseMirror";

import { getExperienceTextByType } from "../ProfileExperience/ExperienceCard";

export enum RefereeType {
  WorkedWith,
  DidntWorkWith,
}

export enum RefereeGroupName {
  WorkedWith = "You worked together",
  DidntWorkWith = "Didn’t work together",
}

interface IRefereeExperienceAssigned {
  groupName: RefereeGroupName;
  applicantProjectList?: ApplicantProjectFull[];
  applicantMilestoneList?: ApplicantMilestoneFull[];
  applicantPublicTalkList?: ApplicantPublicTalk[];
  applicantContestList?: ApplicantContest[];
  applicantOpenSourceList?: ApplicantOpenSource[];
  applicantMentorshipList?: ApplicantMentorship[];
  applicantTeachingList?: ApplicantEducation[];
  applicantCommunityList?: ApplicantCommunity[];
  applicantAnotherExperienceList?: ApplicantAnotherExperience[];
  applicantArticleList?: ApplicantArticle[];
  applicantAwardList?: ApplicantAward[];
  applicantPatentList?: ApplicantPatent[];
  applicationProfJourneyFormList?: ApplicationFormPrepared[];
}

export const getRefereeType = (referee: ApplicantReferee): RefereeType => {
  return referee.workedTogether
    ? RefereeType.WorkedWith
    : RefereeType.DidntWorkWith;
};

export const getRefereeAssignedExperience = (
  arg: IRefereeExperienceAssigned,
  t: TFunction
): DropdownOption[] => {
  const applicantExperienceOptions: DropdownOption[] = [];

  applicantExperienceOptions.push({
    title: t(
      "application:referees.potential.jointProject.dropdown.generalInfo"
    ),
    value: "general-info",
  });

  // New prof journey projects
  if (arg.groupName === RefereeGroupName.WorkedWith) {
    arg.applicationProfJourneyFormList?.forEach((x) => {
      const companyName = x.fieldsMap.name?.value;
      const experienceType = x.fieldsMap.experienceType?.value;

      if (companyName && experienceType) {
        x.children?.forEach((project: ApplicationFormPrepared) => {
          const projectName = project.fieldsMap.name?.value;
          const projectId = project.id;

          if (
            projectName &&
            projectId &&
            project.statusId === ApplicationSectionStatusType.ReviewCompleted
          ) {
            applicantExperienceOptions.push({
              title: `${projectName} at ${companyName}`,
              value: projectId,
              group: getExperienceTextByType(
                experienceType as ExperienceType,
                t
              ),
            });
          }
        });
      }
    });

    arg.applicantProjectList?.forEach((x) => {
      applicantExperienceOptions.push({
        title: `${parseProseMirrorSchema(x.name)}${
          x.applicantCompany
            ? ` at ${parseProseMirrorSchema(x.applicantCompany.name)}`
            : ""
        }`,
        value: x.id,
        group: getExperienceTextByType(ExperienceType.Employment, t),
      });
    });

    arg.applicantMilestoneList?.forEach((x) => {
      applicantExperienceOptions.push({
        title: `${parseProseMirrorSchema(x.name)}${
          x.applicantEntrepreneurship
            ? ` at ${parseProseMirrorSchema(x.applicantEntrepreneurship.name)}`
            : ""
        }`,
        value: x.id,
        group: getExperienceTextByType(ExperienceType.Entrepreneurship, t),
      });
    });
  } else if (arg.groupName === RefereeGroupName.DidntWorkWith) {
    arg.applicantPublicTalkList?.forEach((x) => {
      applicantExperienceOptions.push({
        title: parseProseMirrorSchema(x.name),
        value: x.id,
        group: getExperienceTextByType(ExperienceType.PublicTalks, t),
      });
    });

    arg.applicantContestList?.forEach((x) => {
      applicantExperienceOptions.push({
        title: parseProseMirrorSchema(x.name),
        value: x.id,
        group: getExperienceTextByType(ExperienceType.Contests, t),
      });
    });

    arg.applicantOpenSourceList?.forEach((x) => {
      applicantExperienceOptions.push({
        title: parseProseMirrorSchema(x.name),
        value: x.id,
        group: getExperienceTextByType(
          ExperienceType.OpenSourceContribution,
          t
        ),
      });
    });

    arg.applicantMentorshipList?.forEach((x) => {
      applicantExperienceOptions.push({
        title: parseProseMirrorSchema(x.name),
        value: x.id,
        group: getExperienceTextByType(ExperienceType.TeachingAndMentorship, t),
      });
    });

    arg.applicantCommunityList?.forEach((x) => {
      applicantExperienceOptions.push({
        title: parseProseMirrorSchema(x.name),
        value: x.id,
        group: getExperienceTextByType(ExperienceType.CommunityDevelopment, t),
      });
    });

    arg.applicantAnotherExperienceList?.forEach((x) => {
      applicantExperienceOptions.push({
        title: parseProseMirrorSchema(x.name),
        value: x.id,
        group: getExperienceTextByType(ExperienceType.AnotherExperience, t),
      });
    });

    arg.applicantArticleList?.forEach((x) => {
      applicantExperienceOptions.push({
        title: parseProseMirrorSchema(x.name),
        value: x.id,
        group: getExperienceTextByType(ExperienceType.Articles, t),
      });
    });

    arg.applicantAwardList?.forEach((x) => {
      applicantExperienceOptions.push({
        title: parseProseMirrorSchema(x.name),
        value: x.id,
        group: getExperienceTextByType(ExperienceType.Award, t),
      });
    });

    arg.applicantPatentList?.forEach((x) => {
      applicantExperienceOptions.push({
        title: parseProseMirrorSchema(x.name),
        value: x.id,
        group: getExperienceTextByType(ExperienceType.Patent, t),
      });
    });
  }

  return applicantExperienceOptions;
};

interface IRefereeExternalActivities {
  applicantPublicTalkList?: ApplicantPublicTalk[];
  applicantContestList?: ApplicantContest[];
  applicantOpenSourceList?: ApplicantOpenSource[];
  applicantMentorshipList?: ApplicantMentorship[];
  applicantTeachingList?: ApplicantEducation[];
  applicantCommunityList?: ApplicantCommunity[];
  applicantAnotherExperienceList?: ApplicantAnotherExperience[];
  applicantArticleList?: ApplicantArticle[];
  applicantAwardList?: ApplicantAward[];
  applicantPatentList?: ApplicantPatent[];
}

export const getRefereeExternalActivities = (
  arg: IRefereeExternalActivities,
  t: TFunction
): DropdownOption[] => {
  const externalActivitiesOptions: DropdownOption[] = [];

  // Articles
  arg.applicantArticleList?.forEach((x) => {
    externalActivitiesOptions.push({
      title: `Article: ${parseProseMirrorSchema(x.name)}`,
      value: x.id,
      group: getExperienceTextByType(ExperienceType.Articles, t),
    });
  });

  // Awards
  arg.applicantAwardList?.forEach((x) => {
    externalActivitiesOptions.push({
      title: `Award: ${parseProseMirrorSchema(x.name)}`,
      value: x.id,
      group: getExperienceTextByType(ExperienceType.Award, t),
    });
  });

  // Contests
  arg.applicantContestList?.forEach((x) => {
    externalActivitiesOptions.push({
      title: `Contest: ${parseProseMirrorSchema(x.name)}`,
      value: x.id,
      group: getExperienceTextByType(ExperienceType.Contests, t),
    });
  });

  // Public Talks
  arg.applicantPublicTalkList?.forEach((x) => {
    externalActivitiesOptions.push({
      title: `Public Talk: ${parseProseMirrorSchema(x.name)}`,
      value: x.id,
      group: getExperienceTextByType(ExperienceType.PublicTalks, t),
    });
  });

  // Open Source
  arg.applicantOpenSourceList?.forEach((x) => {
    externalActivitiesOptions.push({
      title: `Open Source: ${parseProseMirrorSchema(x.name)}`,
      value: x.id,
      group: getExperienceTextByType(ExperienceType.OpenSourceContribution, t),
    });
  });

  // Mentorship
  arg.applicantMentorshipList?.forEach((x) => {
    externalActivitiesOptions.push({
      title: `Mentorship: ${parseProseMirrorSchema(x.name)}`,
      value: x.id,
      group: getExperienceTextByType(ExperienceType.TeachingAndMentorship, t),
    });
  });

  // Teaching
  arg.applicantTeachingList?.forEach((x) => {
    externalActivitiesOptions.push({
      title: `Teaching: ${parseProseMirrorSchema(x.name)}`,
      value: x.id,
      group: getExperienceTextByType(ExperienceType.TeachingAndMentorship, t),
    });
  });

  // Community
  arg.applicantCommunityList?.forEach((x) => {
    externalActivitiesOptions.push({
      title: `Community: ${parseProseMirrorSchema(x.name)}`,
      value: x.id,
      group: getExperienceTextByType(ExperienceType.CommunityDevelopment, t),
    });
  });

  // Patents
  arg.applicantPatentList?.forEach((x) => {
    externalActivitiesOptions.push({
      title: `Patent: ${parseProseMirrorSchema(x.name)}`,
      value: x.id,
      group: getExperienceTextByType(ExperienceType.Patent, t),
    });
  });

  // Another Experience
  arg.applicantAnotherExperienceList?.forEach((x) => {
    externalActivitiesOptions.push({
      title: `Another Experience: ${parseProseMirrorSchema(x.name)}`,
      value: x.id,
      group: getExperienceTextByType(ExperienceType.AnotherExperience, t),
    });
  });

  // Sort by created date (newest first, then we'll reverse for display)
  return externalActivitiesOptions.sort((_a, _b) => {
    // We'll need to get the actual entities to sort by date
    // For now, return as is - we'll sort in the component if needed
    return 0;
  });
};
